import React, { useState, useEffect, useRef } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import Image from '../../../components/AppImage';
import { useAuth } from '../../../contexts/AuthContext';
import { uploadImage, getImageUrl } from '../../../services/storageService';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import ImageHotspotEditor from './ImageHotspotEditor';

const SortableContentBlock = ({ item, editingContentId, onEdit, onRemove }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: item?.id });

  const style = {
    transform: CSS?.Transform?.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 10 : 'auto',
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      onClick={() => onEdit(item)}
      className={`p-3 bg-card rounded-lg border group hover:border-primary transition-organic cursor-pointer ${
        editingContentId === item?.id ? 'border-primary ring-2 ring-primary/20' : 'border-border'
      } ${isDragging ? 'shadow-lg' : ''}`}
    >
      <div className="flex items-start justify-between mb-2">
        <div className="flex items-center">
          <button
            {...attributes}
            {...listeners}
            onClick={(e) => e?.stopPropagation()}
            className="mr-1 p-0.5 rounded cursor-grab active:cursor-grabbing text-muted-foreground hover:text-foreground transition-organic flex-shrink-0"
            title="Drag to reorder"
          >
            <Icon name="GripVertical" size={14} />
          </button>
          <Icon
            name={
              item?.type === 'text' ? 'FileText' :
              item?.type === 'image' ? 'Image' :
              item?.type === 'video' ? 'Video' :
              item?.type === 'quiz' ? 'HelpCircle' : 'Gamepad2'
            }
            size={16}
            className="text-primary mr-2"
          />
          <span className="text-xs font-medium text-muted-foreground uppercase">
            {item?.type}
          </span>
          {editingContentId === item?.id && (
            <span className="ml-2 text-xs font-medium text-primary">
              (Editing)
            </span>
          )}
        </div>
        <button
          onClick={(e) => {
            e?.stopPropagation();
            onRemove(item?.id);
          }}
          className="opacity-0 group-hover:opacity-100 transition-organic p-1 hover:bg-destructive/10 rounded"
        >
          <Icon name="X" size={14} className="text-destructive" />
        </button>
      </div>
      {item?.type === 'text' && (
        <p className="text-sm text-foreground line-clamp-3">
          {item?.content}
        </p>
      )}
      {item?.type === 'image' && (
        <div className="w-full h-24 rounded overflow-hidden bg-muted">
          <Image
            src={item?.url}
            alt="Preview of uploaded educational content image"
            className="w-full h-full object-cover"
          />
        </div>
      )}
      {item?.type === 'video' && (
        <p className="text-xs text-muted-foreground truncate">
          {item?.url}
        </p>
      )}
      {item?.type === 'quiz' && (
        <p className="text-sm text-foreground line-clamp-2">
          {item?.question}
        </p>
      )}
      {item?.type === 'game' && (
        <div>
          <p className="text-sm text-foreground font-medium capitalize">
            {item?.title || `${item?.gameType?.replace('-', ' ')} Game`}
          </p>
          {item?.description && (
            <p className="text-xs text-muted-foreground mt-1">
              {item?.description}
            </p>
          )}
        </div>
      )}
    </div>
  );
};

const CreativeStudioStep = ({ content, onChange }) => {
  const { user } = useAuth();
  const [currentPage, setCurrentPage] = useState(1);
  const [activeTab, setActiveTab] = useState('text');
  const [textContent, setTextContent] = useState('');
  const [videoUrl, setVideoUrl] = useState('');
  const [quizQuestion, setQuizQuestion] = useState('');
  const [quizOptions, setQuizOptions] = useState(['', '', '', '']);
  const [correctAnswer, setCorrectAnswer] = useState('');
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [selectedGameType, setSelectedGameType] = useState(null);
  const [editingContentId, setEditingContentId] = useState(null);
  const [completionTitle, setCompletionTitle] = useState('Congratulations!');
  const [completionMessage, setCompletionMessage] = useState('You have completed this learning trail!');
  const [gameConfig, setGameConfig] = useState({
    title: '',
    description: '',
    pairs: [{ term: '', definition: '', termType: 'text', definitionType: 'text', termImage: '', definitionImage: '' }],
    words: [''],
    gridSize: '10x10',
    items: [{ id: 1, text: '', itemType: 'text', itemImage: '', correctPosition: 0 }],
    statement: '',
    answer: '',
    blanks: [{ sentence: '', answer: '' }],
    categories: [{ name: '', items: [{ text: '', itemType: 'text', itemImage: '' }] }],
    hotspotImage: '',
    hotspots: []
  });

  // Auto-set current page to first available page when content loads
  useEffect(() => {
    if (content && content?.length > 0) {
      const allPages = [...new Set(content?.map(item => item?.pageNumber || 1))]?.sort((a, b) => a - b);
      if (allPages?.length > 0 && !allPages?.includes(currentPage)) {
        // If current page doesn't exist in content, switch to first available page
        setCurrentPage(allPages?.[0]);
      }
    }
  }, [content]);

  // Get content for current page
  const currentPageContent = content?.filter(item => item?.pageNumber === currentPage && item?.type !== 'page_marker') || [];
  
  // Get all unique page numbers
  const allPages = [...new Set(content?.map(item => item?.pageNumber || 1))]?.sort((a, b) => a - b);
  const totalPages = allPages?.length || 1;

  // Check if completion page exists
  const hasCompletionPage = content?.some(item => item?.type === 'completion');

  const contentTypes = [
    { id: 'text', label: 'Text Content', icon: 'FileText' },
    { id: 'image', label: 'Images', icon: 'Image' },
    { id: 'video', label: 'Videos', icon: 'Video' },
    { id: 'quiz', label: 'Quiz', icon: 'HelpCircle' },
    { id: 'game', label: 'Games', icon: 'Gamepad2' }
  ];

  const templateOptions = [
    { value: '', label: 'Choose a template' },
    { value: 'intro', label: 'Introduction Section' },
    { value: 'concept', label: 'Key Concept Explanation' },
    { value: 'example', label: 'Practical Example' },
    { value: 'activity', label: 'Interactive Activity' },
    { value: 'summary', label: 'Summary & Review' }
  ];

  const templates = {
    intro: `📚 Introduction to [Topic Name]

Welcome to this learning journey! In this section, we'll explore [main topic].

🎯 Learning Objectives:
• Understand the fundamental concepts of [topic]
• Identify key components and their relationships
• Apply knowledge to real-world scenarios

💡 Why This Matters:
[Explain the relevance and importance of this topic to students' learning and future applications]

🚀 What You'll Learn:
By the end of this section, you will be able to [list specific outcomes].

Let's begin our exploration!`,
    
    concept: `🔑 Key Concept: [Concept Name]

📖 Definition:
[Provide a clear, student-friendly definition of the concept]

🧩 Breaking It Down:
1. Component 1: [Explanation]
2. Component 2: [Explanation]
3. Component 3: [Explanation]

💭 Think About It:
[Pose a thought-provoking question or scenario to help students connect with the concept]

📌 Key Points to Remember:
• Point 1
• Point 2
• Point 3

🔗 Connection to Previous Learning:
[Explain how this concept builds on or relates to what students already know]`,
    
    example: `📝 Practical Example: [Example Title]

🌍 Real-World Scenario:
[Describe a relatable, real-world situation where this concept applies]

🔍 Let's Analyze:
Step 1: [Describe the first step]
→ What happens: [Explanation]
→ Why it matters: [Significance]

Step 2: [Describe the second step]
→ What happens: [Explanation]
→ Why it matters: [Significance]

Step 3: [Describe the third step]
→ What happens: [Explanation]
→ Why it matters: [Significance]

✅ Outcome:
[Explain the result and what students should learn from this example]

🤔 Your Turn:
Can you think of another situation where this concept applies? [Prompt for student reflection]`,
    
    activity: `🎮 Interactive Activity: [Activity Name]

🎯 Objective:
[State what students will practice or demonstrate through this activity]

📋 Instructions:
1. [First instruction]
2. [Second instruction]
3. [Third instruction]
4. [Fourth instruction]

⏱️ Time Required: [Estimated time]

💪 Challenge Level: [Beginner/Intermediate/Advanced]

✨ Tips for Success:
• Tip 1: [Helpful guidance]
• Tip 2: [Helpful guidance]
• Tip 3: [Helpful guidance]

🏆 Success Criteria:
You'll know you've mastered this when you can:
□ [Criterion 1]
□ [Criterion 2]
□ [Criterion 3]

💬 Reflection:
After completing this activity, consider: [Reflection question]`,
    
    summary: `📊 Summary & Review: [Section Title]

🎓 What We've Learned:
In this section, we covered:
• [Key point 1]
• [Key point 2]
• [Key point 3]
• [Key point 4]

🔑 Essential Takeaways:
1. [Most important concept 1]
2. [Most important concept 2]
3. [Most important concept 3]

🔄 Quick Review:
[Provide a brief recap of the main ideas in 2-3 sentences]

✅ Self-Check:
Before moving forward, make sure you can:
□ [Skill/knowledge 1]
□ [Skill/knowledge 2]
□ [Skill/knowledge 3]

➡️ Next Steps:
[Preview what comes next in the learning journey]

💡 Additional Resources:
• [Resource suggestion 1]
• [Resource suggestion 2]

❓ Questions? Review the sections above or ask your teacher for clarification!`
  };

  const handleTemplateSelect = (templateValue) => {
    setSelectedTemplate(templateValue);
    if (templateValue && templates?.[templateValue]) {
      setTextContent(templates?.[templateValue]);
      setActiveTab('text');
    }
  };

  const gameTemplates = [
    { id: 'memory-match', name: 'Memory Match', description: 'Match pairs of cards with terms and definitions', icon: 'Puzzle' },
    { id: 'word-search', name: 'Word Search', description: 'Find hidden words in a letter grid', icon: 'Search' },
    { id: 'drag-drop', name: 'Drag & Drop', description: 'Arrange items in the correct order or position', icon: 'Move' },
    { id: 'true-false', name: 'True/False', description: 'Determine if statements are true or false', icon: 'CheckCircle' },
    { id: 'fill-blanks', name: 'Fill in the Blanks', description: 'Complete sentences with missing words', icon: 'Type' },
    { id: 'sorting', name: 'Sorting Game', description: 'Categorize items into correct groups', icon: 'ArrowUpDown' },
    { id: 'image-hotspot', name: 'Image Hotspot', description: 'Click on image areas to answer questions', icon: 'MapPin' }
  ];

  const handleAddContent = (type) => {
    // If editing existing content, update it instead of adding new
    if (editingContentId) {
      const updatedContent = content?.map(item => {
        if (item?.id === editingContentId) {
          const updated = { ...item };
          
          switch (type) {
            case 'text':
              updated.content = textContent;
              break;
            case 'video':
              updated.url = videoUrl;
              break;
            case 'quiz':
              updated.question = quizQuestion;
              updated.options = quizOptions;
              updated.correctAnswer = correctAnswer;
              break;
            default:
              break;
          }
          
          return updated;
        }
        return item;
      });
      
      onChange(updatedContent);
      
      // Clear editing state and form fields
      setEditingContentId(null);
      setTextContent('');
      setVideoUrl('');
      setQuizQuestion('');
      setQuizOptions(['', '', '', '']);
      setCorrectAnswer('');
      
      return;
    }
    
    // Otherwise, add new content
    const newContent = {
      id: Date.now(),
      type,
      pageNumber: currentPage,
      timestamp: new Date()?.toISOString()
    };

    switch (type) {
      case 'text':
        if (textContent?.trim()) {
          newContent.content = textContent;
          onChange([...content?.filter(item => !(item?.type === 'page_marker' && item?.pageNumber === currentPage)), newContent]);
          setTextContent('');
        }
        break;
      case 'video':
        if (videoUrl?.trim()) {
          newContent.url = videoUrl;
          onChange([...content?.filter(item => !(item?.type === 'page_marker' && item?.pageNumber === currentPage)), newContent]);
          setVideoUrl('');
        }
        break;
      case 'quiz':
        if (quizQuestion?.trim() && quizOptions?.every(opt => opt?.trim()) && correctAnswer) {
          newContent.question = quizQuestion;
          newContent.options = quizOptions;
          newContent.correctAnswer = correctAnswer;
          onChange([...content?.filter(item => !(item?.type === 'page_marker' && item?.pageNumber === currentPage)), newContent]);
          setQuizQuestion('');
          setQuizOptions(['', '', '', '']);
          setCorrectAnswer('');
        }
        break;
      default:
        break;
    }
  };

  const handleImageUpload = (e) => {
    const file = e?.target?.files?.[0];
    if (file) {
      if (file?.size > 5 * 1024 * 1024) {
        alert('File size must be less than 5MB');
        return;
      }
      const reader = new FileReader();
      reader.onloadend = () => {
        const newContent = {
          id: Date.now(),
          type: 'image',
          url: reader?.result,
          pageNumber: currentPage,
          timestamp: new Date()?.toISOString()
        };
        onChange([...content?.filter(item => !(item?.type === 'page_marker' && item?.pageNumber === currentPage)), newContent]);
      };
      reader?.readAsDataURL(file);
    }
  };

  const handleRemoveContent = (id) => {
    onChange(content?.filter(item => item?.id !== id));
  };

  const handleAddNewPage = () => {
    // Get the completion page number if it exists
    const completionItem = content?.find(item => item?.type === 'completion');
    const nonCompletionPages = allPages?.filter(p => p !== completionItem?.pageNumber);
    const newPageNumber = Math.max(...nonCompletionPages, 0) + 1;

    // Add a page marker so the new page appears in allPages
    const pageMarker = {
      id: `page_marker_${newPageNumber}_${Date.now()}`,
      type: 'page_marker',
      pageNumber: newPageNumber,
      timestamp: new Date()?.toISOString()
    };

    if (completionItem) {
      // Shift the completion page up by 1 to make room
      const updatedContent = content?.map(item =>
        item?.pageNumber === completionItem?.pageNumber
          ? { ...item, pageNumber: item?.pageNumber + 1 }
          : item
      );
      onChange([...updatedContent, pageMarker]);
    } else {
      onChange([...content, pageMarker]);
    }

    setCurrentPage(newPageNumber);
  };

  const handleDeletePage = (pageNum) => {
    if (allPages?.length <= 1) {
      alert('Cannot delete the only page');
      return;
    }
    
    const confirmDelete = window.confirm(`Delete page ${pageNum} and all its content?`);
    if (confirmDelete) {
      const updatedContent = content?.filter(item => item?.pageNumber !== pageNum);
      onChange(updatedContent);
      
      // Navigate to previous page if current page was deleted
      if (currentPage === pageNum) {
        setCurrentPage(Math.max(1, pageNum - 1));
      }
    }
  };

  const handleAddCompletionPage = () => {
    if (hasCompletionPage) {
      alert('Completion page already exists');
      return;
    }

    const completionPageNumber = Math.max(...allPages, 0) + 1;
    const newContent = {
      id: Date.now(),
      type: 'completion',
      pageNumber: completionPageNumber,
      title: completionTitle,
      message: completionMessage,
      showCertificate: false,
      timestamp: new Date()?.toISOString()
    };
    onChange([...content, newContent]);
    setCurrentPage(completionPageNumber);
  };

  const handleDeleteCompletionPage = () => {
    const confirmDelete = window.confirm('Delete the completion page?');
    if (confirmDelete) {
      const completionPage = content?.find(item => item?.type === 'completion');
      if (completionPage) {
        const updatedContent = content?.filter(item => item?.pageNumber !== completionPage?.pageNumber);
        onChange(updatedContent);
        if (currentPage === completionPage?.pageNumber) {
          const remainingPages = [...new Set(updatedContent?.map(item => item?.pageNumber || 1))]?.sort((a, b) => a - b);
          setCurrentPage(remainingPages?.[0] || 1);
        }
      }
    }
  };

  const handleAddGame = (gameType) => {
    setSelectedGameType(gameType);
    setGameConfig({
      title: '',
      description: '',
      pairs: [{ term: '', definition: '', termType: 'text', definitionType: 'text', termImage: '', definitionImage: '' }],
      words: [''],
      gridSize: '10x10',
      items: [{ id: 1, text: '', itemType: 'text', itemImage: '', correctPosition: 0 }],
      statement: '',
      answer: '',
      blanks: [{ sentence: '', answer: '' }],
      categories: [{ name: '', items: [{ text: '', itemType: 'text', itemImage: '' }] }],
      hotspotImage: '',
      hotspots: []
    });
  };

  const handleCancelEdit = () => {
    setEditingContentId(null);
    setSelectedGameType(null);
    setGameConfig({
      title: '',
      description: '',
      pairs: [{ term: '', definition: '', termType: 'text', definitionType: 'text', termImage: '', definitionImage: '' }],
      words: [''],
      gridSize: '10x10',
      items: [{ id: 1, text: '', itemType: 'text', itemImage: '', correctPosition: 0 }],
      statement: '',
      answer: '',
      blanks: [{ sentence: '', answer: '' }],
      categories: [{ name: '', items: [{ text: '', itemType: 'text', itemImage: '' }] }],
      hotspotImage: '',
      hotspots: []
    });
  };

  const handleEditContent = (item) => {
    setEditingContentId(item?.id);
    
    switch (item?.type) {
      case 'text': setTextContent(item?.content || '');
        setActiveTab('text');
        break;
      case 'video': setVideoUrl(item?.url || '');
        setActiveTab('video');
        break;
      case 'quiz': setQuizQuestion(item?.question || '');
        setQuizOptions(item?.options || ['', '', '', '']);
        setCorrectAnswer(item?.correctAnswer || '');
        setActiveTab('quiz');
        break;
      case 'game':
        setSelectedGameType(item?.gameType);
        setGameConfig({
          title: item?.title || '',
          description: item?.description || '',
          pairs: item?.pairs || item?.gameData?.pairs || [{ term: '', definition: '', termType: 'text', definitionType: 'text', termImage: '', definitionImage: '' }],
          words: item?.words || item?.gameData?.words || [''],
          gridSize: item?.gridSize || item?.gameData?.gridSize || '10x10',
          customWidth: item?.customWidth || item?.gameData?.customWidth || '',
          customHeight: item?.customHeight || item?.gameData?.customHeight || '',
          items: item?.items || item?.gameData?.items || [{ id: 1, text: '', itemType: 'text', itemImage: '', correctPosition: 0 }],
          statement: item?.statement || item?.gameData?.statement || '',
          answer: item?.answer || item?.gameData?.answer || '',
          blanks: item?.blanks || item?.gameData?.blanks || [{ sentence: '', answer: '' }],
          categories: item?.categories || item?.gameData?.categories || [{ name: '', items: [{ text: '', itemType: 'text', itemImage: '' }] }],
          hotspotImage: item?.hotspotImage || item?.gameData?.hotspotImage || '',
          hotspots: item?.hotspots || item?.gameData?.hotspots || []
        });
        setActiveTab('game');
        break;
      default:
        break;
    }
  };

  const handleCancelGame = () => {
    setSelectedGameType(null);
    setEditingContentId(null);
  };

  const handleSaveGame = () => {
    if (!gameConfig?.title?.trim()) {
      alert('Please enter a game title');
      return;
    }

    // Validate game-specific requirements
    if (selectedGameType === 'memory-match' && gameConfig?.pairs?.length < 2) {
      alert('Please add at least 2 pairs for the memory match game');
      return;
    }
    if (selectedGameType === 'word-search' && gameConfig?.words?.filter(w => w?.trim())?.length < 3) {
      alert('Please add at least 3 words for the word search game');
      return;
    }
    if (selectedGameType === 'word-search' && gameConfig?.gridSize === 'custom') {
      const w = parseInt(gameConfig?.customWidth);
      const h = parseInt(gameConfig?.customHeight);
      if (!w || !h || w < 5 || h < 5) {
        alert('Please enter valid custom grid dimensions (minimum 5x5)');
        return;
      }
    }
    if (selectedGameType === 'drag-drop' && gameConfig?.items?.length < 2) {
      alert('Please add at least 2 items for the drag & drop game');
      return;
    }
    if (selectedGameType === 'true-false' && (!gameConfig?.statement?.trim() || !gameConfig?.answer)) {
      alert('Please enter a statement and select the correct answer');
      return;
    }
    if (selectedGameType === 'fill-blanks' && gameConfig?.blanks?.length < 1) {
      alert('Please add at least 1 fill-in-the-blank question');
      return;
    }
    if (selectedGameType === 'sorting' && gameConfig?.categories?.length < 2) {
      alert('Please add at least 2 categories for the sorting game');
      return;
    }
    if (selectedGameType === 'image-hotspot') {
      if (!gameConfig?.hotspotImage) {
        alert('Please upload an image for the hotspot game');
        return;
      }
      if (!gameConfig?.hotspots?.length || gameConfig?.hotspots?.length < 1) {
        alert('Please add at least 1 hotspot to the image');
        return;
      }
      const incomplete = gameConfig?.hotspots?.find(h => !h?.question?.trim() || !h?.correctAnswer);
      if (incomplete) {
        alert('Please fill in the question and correct answer for all hotspots');
        return;
      }
    }

    if (editingContentId) {
      // Update existing game
      const updatedContent = content?.map(item => {
        if (item?.id === editingContentId) {
          return {
            ...item,
            gameType: selectedGameType,
            title: gameConfig?.title,
            description: gameConfig?.description,
            pairs: gameConfig?.pairs,
            words: gameConfig?.words,
            gridSize: gameConfig?.gridSize,
            customWidth: gameConfig?.customWidth,
            customHeight: gameConfig?.customHeight,
            items: gameConfig?.items,
            statement: gameConfig?.statement,
            answer: gameConfig?.answer,
            blanks: gameConfig?.blanks,
            categories: gameConfig?.categories,
            hotspotImage: gameConfig?.hotspotImage,
            hotspots: gameConfig?.hotspots
          };
        }
        return item;
      });
      onChange(updatedContent);
    } else {
      // Add new game
      const newContent = {
        id: Date.now(),
        type: 'game',
        gameType: selectedGameType,
        pageNumber: currentPage,
        timestamp: new Date()?.toISOString(),
        title: gameConfig?.title,
        description: gameConfig?.description,
        pairs: gameConfig?.pairs,
        words: gameConfig?.words,
        gridSize: gameConfig?.gridSize,
        customWidth: gameConfig?.customWidth,
        customHeight: gameConfig?.customHeight,
        items: gameConfig?.items,
        statement: gameConfig?.statement,
        answer: gameConfig?.answer,
        blanks: gameConfig?.blanks,
        categories: gameConfig?.categories,
        hotspotImage: gameConfig?.hotspotImage,
        hotspots: gameConfig?.hotspots
      };
      onChange([...content?.filter(item => !(item?.type === 'page_marker' && item?.pageNumber === currentPage)), newContent]);
    }

    // Reset game configuration
    setSelectedGameType(null);
    setEditingContentId(null);
    setGameConfig({
      title: '',
      description: '',
      pairs: [{ term: '', definition: '', termType: 'text', definitionType: 'text', termImage: '', definitionImage: '' }],
      words: [''],
      gridSize: '10x10',
      items: [{ id: 1, text: '', itemType: 'text', itemImage: '', correctPosition: 0 }],
      statement: '',
      answer: '',
      blanks: [{ sentence: '', answer: '' }],
      categories: [{ name: '', items: [{ text: '', itemType: 'text', itemImage: '' }] }],
      hotspotImage: '',
      hotspots: []
    });
  };

  const addPair = () => {
    setGameConfig(prev => ({
      ...prev,
      pairs: [...prev?.pairs, { term: '', definition: '', termType: 'text', definitionType: 'text', termImage: '', definitionImage: '' }]
    }));
  };

  const updatePair = (index, field, value) => {
    setGameConfig(prev => {
      const newPairs = [...prev?.pairs];
      newPairs[index][field] = value;
      return { ...prev, pairs: newPairs };
    });
  };

  const removePair = (index) => {
    setGameConfig(prev => ({
      ...prev,
      pairs: prev?.pairs?.filter((_, i) => i !== index)
    }));
  };

  const addWord = () => {
    setGameConfig(prev => ({
      ...prev,
      words: [...prev?.words, '']
    }));
  };

  const updateWord = (index, value) => {
    setGameConfig(prev => {
      const newWords = [...prev?.words];
      newWords[index] = value;
      return { ...prev, words: newWords };
    });
  };

  const removeWord = (index) => {
    setGameConfig(prev => ({
      ...prev,
      words: prev?.words?.filter((_, i) => i !== index)
    }));
  };

  const addDragDropItem = () => {
    setGameConfig(prev => ({
      ...prev,
      items: [...prev?.items, { id: prev?.items?.length + 1, text: '', itemType: 'text', itemImage: '', correctPosition: prev?.items?.length }]
    }));
  };

  const updateDragDropItem = (index, field, value) => {
    setGameConfig(prev => {
      const newItems = [...prev?.items];
      newItems[index][field] = value;
      return { ...prev, items: newItems };
    });
  };

  const removeDragDropItem = (index) => {
    setGameConfig(prev => ({
      ...prev,
      items: prev?.items?.filter((_, i) => i !== index)
    }));
  };

  const addBlank = () => {
    setGameConfig(prev => ({
      ...prev,
      blanks: [...prev?.blanks, { sentence: '', answer: '' }]
    }));
  };

  const updateBlank = (index, field, value) => {
    setGameConfig(prev => {
      const newBlanks = [...prev?.blanks];
      newBlanks[index][field] = value;
      return { ...prev, blanks: newBlanks };
    });
  };

  const removeBlank = (index) => {
    setGameConfig(prev => ({
      ...prev,
      blanks: prev?.blanks?.filter((_, i) => i !== index)
    }));
  };

  const addCategory = () => {
    setGameConfig(prev => ({
      ...prev,
      categories: [...prev?.categories, { name: '', items: [{ text: '', itemType: 'text', itemImage: '' }] }]
    }));
  };

  const updateCategory = (index, field, value) => {
    setGameConfig(prev => {
      const newCategories = [...prev?.categories];
      newCategories[index][field] = value;
      return { ...prev, categories: newCategories };
    });
  };

  const removeCategory = (index) => {
    setGameConfig(prev => ({
      ...prev,
      categories: prev?.categories?.filter((_, i) => i !== index)
    }));
  };

  const addCategoryItem = (categoryIndex) => {
    setGameConfig(prev => {
      const newCategories = [...prev?.categories];
      newCategories?.[categoryIndex]?.items?.push({ text: '', itemType: 'text', itemImage: '' });
      return { ...prev, categories: newCategories };
    });
  };

  const updateCategoryItem = (categoryIndex, itemIndex, field, value) => {
    setGameConfig(prev => {
      const newCategories = [...prev?.categories];
      if (typeof newCategories?.[categoryIndex]?.items?.[itemIndex] === 'string') {
        // Migrate legacy string items to object
        newCategories[categoryIndex].items[itemIndex] = { text: newCategories?.[categoryIndex]?.items?.[itemIndex], itemType: 'text', itemImage: '' };
      }
      newCategories[categoryIndex].items[itemIndex] = { ...newCategories?.[categoryIndex]?.items?.[itemIndex], [field]: value };
      return { ...prev, categories: newCategories };
    });
  };

  const removeCategoryItem = (categoryIndex, itemIndex) => {
    setGameConfig(prev => {
      const newCategories = [...prev?.categories];
      newCategories[categoryIndex].items = newCategories?.[categoryIndex]?.items?.filter((_, i) => i !== itemIndex);
      return { ...prev, categories: newCategories };
    });
  };

  // Game image upload helper
  const handleGameImageUpload = async (file, onSuccess, onError) => {
    if (!file) return;
    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
    if (!allowedTypes?.includes(file?.type)) {
      onError?.('Invalid file type. Only JPEG, PNG, WEBP, GIF allowed.');
      return;
    }
    if (file?.size > 10 * 1024 * 1024) {
      onError?.('File size exceeds 10MB limit.');
      return;
    }
    const userId = user?.id || 'anonymous';
    const { filePath, error } = await uploadImage(file, `games/${userId}`);
    if (error) {
      onError?.(error);
      return;
    }
    // Store the file path (not a signed URL) so the reference never expires
    onSuccess?.(filePath);
  };

  // Inline image upload button component
  const GameImageUpload = ({ currentImage, onImageChange, label = 'Upload Image', size = 'sm' }) => {
    const [uploading, setUploading] = useState(false);
    const [uploadError, setUploadError] = useState('');
    const [previewUrl, setPreviewUrl] = useState(null);
    const inputRef = React.useRef(null);

    // Resolve file path to signed URL for preview
    useEffect(() => {
      if (!currentImage) {
        setPreviewUrl(null);
        return;
      }
      // If it's already a full URL, use directly
      if (currentImage?.startsWith('http://') || currentImage?.startsWith('https://')) {
        setPreviewUrl(currentImage);
        return;
      }
      // It's a file path — get a signed URL
      let cancelled = false;
      getImageUrl(currentImage, 3600)?.then(({ url, error }) => {
        if (!cancelled && !error && url) {
          setPreviewUrl(url);
        }
      });
      return () => { cancelled = true; };
    }, [currentImage]);

    const handleFileChange = async (e) => {
      const file = e?.target?.files?.[0];
      if (!file) return;
      setUploading(true);
      setUploadError('');
      await handleGameImageUpload(
        file,
        (url) => { onImageChange(url); setUploading(false); },
        (err) => { setUploadError(err); setUploading(false); }
      );
      e.target.value = '';
    };

    return (
      <div className="space-y-2">
        <input ref={inputRef} type="file" accept="image/*" className="hidden" onChange={handleFileChange} />
        {currentImage ? (
          <div className="relative inline-block">
            {previewUrl ? (
              <img
                src={previewUrl}
                alt="Uploaded game card image"
                className="w-24 h-24 object-cover rounded-lg border-2 border-primary"
              />
            ) : (
              <div className="w-24 h-24 rounded-lg border-2 border-primary bg-muted flex items-center justify-center">
                <Icon name="Loader2" size={20} className="animate-spin text-muted-foreground" />
              </div>
            )}
            <button
              type="button"
              onClick={() => onImageChange('')}
              className="absolute -top-2 -right-2 w-5 h-5 bg-destructive text-white rounded-full flex items-center justify-center hover:bg-destructive/80"
            >
              <Icon name="X" size={10} />
            </button>
            <button
              type="button"
              onClick={() => inputRef?.current?.click()}
              className="absolute bottom-0 right-0 w-6 h-6 bg-primary text-white rounded-full flex items-center justify-center hover:bg-primary/80"
            >
              <Icon name="RefreshCw" size={10} />
            </button>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => inputRef?.current?.click()}
            disabled={uploading}
            className="flex items-center space-x-2 px-3 py-2 border-2 border-dashed border-border rounded-lg hover:border-primary text-sm text-muted-foreground hover:text-primary transition-all"
          >
            {uploading ? (
              <><Icon name="Loader2" size={14} className="animate-spin" /><span>Uploading...</span></>
            ) : (
              <><Icon name="Upload" size={14} /><span>{label}</span></>
            )}
          </button>
        )}
        {uploadError && <p className="text-xs text-destructive">{uploadError}</p>}
      </div>
    );
  };

  // Text/Image toggle component
  const TypeToggle = ({ value, onChange }) => (
    <div className="flex rounded-md border border-border overflow-hidden">
      <button
        type="button"
        onClick={() => onChange('text')}
        className={`px-3 py-1.5 text-xs font-medium transition-all ${
          value === 'text' ? 'bg-primary text-white' : 'bg-card text-muted-foreground hover:bg-muted'
        }`}
      >
        Text
      </button>
      <button
        type="button"
        onClick={() => onChange('image')}
        className={`px-3 py-1.5 text-xs font-medium transition-all ${
          value === 'image' ? 'bg-primary text-white' : 'bg-card text-muted-foreground hover:bg-muted'
        }`}
      >
        Image
      </button>
    </div>
  );

  const renderGameConfiguration = () => {
    if (!selectedGameType) return null;

    const gameTemplate = gameTemplates?.find(g => g?.id === selectedGameType);

    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
              <Icon name={gameTemplate?.icon} size={20} className="text-primary" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-foreground">{gameTemplate?.name}</h3>
              <p className="text-sm text-muted-foreground">{gameTemplate?.description}</p>
            </div>
          </div>
          <Button variant="ghost" iconName="X" onClick={handleCancelGame} />
        </div>
        <div className="space-y-4">
          <Input
            label="Game Title"
            type="text"
            placeholder="Enter a title for this game"
            value={gameConfig?.title}
            onChange={(e) => setGameConfig(prev => ({ ...prev, title: e?.target?.value }))}
            required
          />
          <Input
            label="Description (Optional)"
            type="text"
            placeholder="Brief instructions or context"
            value={gameConfig?.description}
            onChange={(e) => setGameConfig(prev => ({ ...prev, description: e?.target?.value }))}
          />
        </div>
        {selectedGameType === 'memory-match' && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <label className="text-sm font-medium text-foreground">Matching Pairs</label>
              <Button variant="outline" size="sm" iconName="Plus" onClick={addPair}>
                Add Pair
              </Button>
            </div>
            {gameConfig?.pairs?.map((pair, index) => (
              <div key={index} className="p-4 bg-muted/30 rounded-lg space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-muted-foreground">Pair {index + 1}</span>
                  {gameConfig?.pairs?.length > 1 && (
                    <button onClick={() => removePair(index)} className="text-destructive hover:text-destructive/80">
                      <Icon name="Trash2" size={16} />
                    </button>
                  )}
                </div>
                {/* Card A */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-muted-foreground font-medium">Card A</span>
                    <TypeToggle
                      value={pair?.termType || 'text'}
                      onChange={(val) => updatePair(index, 'termType', val)}
                    />
                  </div>
                  {(pair?.termType || 'text') === 'text' ? (
                    <Input
                      type="text"
                      placeholder="Term or question"
                      value={pair?.term}
                      onChange={(e) => updatePair(index, 'term', e?.target?.value)}
                    />
                  ) : (
                    <div className="space-y-1">
                      <GameImageUpload
                        currentImage={pair?.termImage}
                        onImageChange={(url) => updatePair(index, 'termImage', url)}
                        label="Upload card image"
                      />
                      <Input
                        type="text"
                        placeholder="Alt text / label (optional)"
                        value={pair?.term}
                        onChange={(e) => updatePair(index, 'term', e?.target?.value)}
                      />
                    </div>
                  )}
                </div>
                {/* Card B */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-muted-foreground font-medium">Card B</span>
                    <TypeToggle
                      value={pair?.definitionType || 'text'}
                      onChange={(val) => updatePair(index, 'definitionType', val)}
                    />
                  </div>
                  {(pair?.definitionType || 'text') === 'text' ? (
                    <Input
                      type="text"
                      placeholder="Definition or answer"
                      value={pair?.definition}
                      onChange={(e) => updatePair(index, 'definition', e?.target?.value)}
                    />
                  ) : (
                    <div className="space-y-1">
                      <GameImageUpload
                        currentImage={pair?.definitionImage}
                        onImageChange={(url) => updatePair(index, 'definitionImage', url)}
                        label="Upload card image"
                      />
                      <Input
                        type="text"
                        placeholder="Alt text / label (optional)"
                        value={pair?.definition}
                        onChange={(e) => updatePair(index, 'definition', e?.target?.value)}
                      />
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
        {selectedGameType === 'word-search' && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <label className="text-sm font-medium text-foreground">Words to Find</label>
              <Button variant="outline" size="sm" iconName="Plus" onClick={addWord}>
                Add Word
              </Button>
            </div>
            <Select
              label="Grid Size"
              options={[
                { value: '10x10', label: '10x10 (Easy)' },
                { value: '15x15', label: '15x15 (Medium)' },
                { value: '20x20', label: '20x20 (Hard)' },
                { value: 'custom', label: 'Custom' }
              ]}
              value={gameConfig?.gridSize}
              onChange={(value) => setGameConfig(prev => ({ ...prev, gridSize: value }))}
            />
            {gameConfig?.gridSize === 'custom' && (
              <div className="flex items-center space-x-3">
                <div className="flex-1">
                  <label className="block text-xs font-medium text-muted-foreground mb-1">Width (columns)</label>
                  <Input
                    type="number"
                    placeholder="e.g. 12"
                    min={5}
                    max={50}
                    value={gameConfig?.customWidth || ''}
                    onChange={(e) => setGameConfig(prev => ({ ...prev, customWidth: e?.target?.value }))}
                  />
                </div>
                <div className="flex items-center pt-5 text-muted-foreground font-medium">×</div>
                <div className="flex-1">
                  <label className="block text-xs font-medium text-muted-foreground mb-1">Height (rows)</label>
                  <Input
                    type="number"
                    placeholder="e.g. 12"
                    min={5}
                    max={50}
                    value={gameConfig?.customHeight || ''}
                    onChange={(e) => setGameConfig(prev => ({ ...prev, customHeight: e?.target?.value }))}
                  />
                </div>
              </div>
            )}
            {gameConfig?.words?.map((word, index) => (
              <div key={index} className="flex items-center space-x-2">
                <Input
                  type="text"
                  placeholder={`Word ${index + 1}`}
                  value={word}
                  onChange={(e) => updateWord(index, e?.target?.value)}
                />
                {gameConfig?.words?.length > 1 && (
                  <button onClick={() => removeWord(index)} className="text-destructive hover:text-destructive/80">
                    <Icon name="Trash2" size={16} />
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
        {selectedGameType === 'drag-drop' && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <label className="text-sm font-medium text-foreground">Items to Arrange</label>
              <Button variant="outline" size="sm" iconName="Plus" onClick={addDragDropItem}>
                Add Item
              </Button>
            </div>
            <p className="text-sm text-muted-foreground">Students will drag items to arrange them in the correct order (1 = first, 2 = second, etc.)</p>
            {gameConfig?.items?.map((item, index) => (
              <div key={index} className="p-4 bg-muted/30 rounded-lg space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-muted-foreground">Item {index + 1}</span>
                  <div className="flex items-center space-x-2">
                    <TypeToggle
                      value={item?.itemType || 'text'}
                      onChange={(val) => updateDragDropItem(index, 'itemType', val)}
                    />
                    {gameConfig?.items?.length > 2 && (
                      <button onClick={() => removeDragDropItem(index)} className="text-destructive hover:text-destructive/80">
                        <Icon name="Trash2" size={16} />
                      </button>
                    )}
                  </div>
                </div>
                {(item?.itemType || 'text') === 'text' ? (
                  <Input
                    type="text"
                    placeholder="Item text"
                    value={item?.text}
                    onChange={(e) => updateDragDropItem(index, 'text', e?.target?.value)}
                  />
                ) : (
                  <div className="space-y-1">
                    <GameImageUpload
                      currentImage={item?.itemImage}
                      onImageChange={(url) => updateDragDropItem(index, 'itemImage', url)}
                      label="Upload item image"
                    />
                    <Input
                      type="text"
                      placeholder="Alt text / label (optional)"
                      value={item?.text}
                      onChange={(e) => updateDragDropItem(index, 'text', e?.target?.value)}
                    />
                  </div>
                )}
                <Input
                  type="number"
                  label="Correct Position"
                  placeholder="1, 2, 3..."
                  value={item?.correctPosition}
                  onChange={(e) => updateDragDropItem(index, 'correctPosition', parseInt(e?.target?.value) || 0)}
                  min="0"
                />
              </div>
            ))}
          </div>
        )}
        {selectedGameType === 'true-false' && (
          <div className="space-y-4">
            <Input
              label="Statement"
              type="text"
              placeholder="Enter a statement for students to evaluate"
              value={gameConfig?.statement}
              onChange={(e) => setGameConfig(prev => ({ ...prev, statement: e?.target?.value }))}
            />
            <Select
              label="Correct Answer"
              options={[
                { value: '', label: 'Select answer' },
                { value: 'true', label: 'True' },
                { value: 'false', label: 'False' }
              ]}
              value={gameConfig?.answer}
              onChange={(value) => setGameConfig(prev => ({ ...prev, answer: value }))}
            />
          </div>
        )}
        {selectedGameType === 'fill-blanks' && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <label className="text-sm font-medium text-foreground">Fill in the Blank Questions</label>
              <Button variant="outline" size="sm" iconName="Plus" onClick={addBlank}>
                Add Question
              </Button>
            </div>
            <p className="text-sm text-muted-foreground">Use _____ (5 underscores) to indicate where the blank should appear</p>
            {gameConfig?.blanks?.map((blank, index) => (
              <div key={index} className="p-4 bg-muted/30 rounded-lg space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-muted-foreground">Question {index + 1}</span>
                  {gameConfig?.blanks?.length > 1 && (
                    <button onClick={() => removeBlank(index)} className="text-destructive hover:text-destructive/80">
                      <Icon name="Trash2" size={16} />
                    </button>
                  )}
                </div>
                <Input
                  type="text"
                  placeholder="The capital of France is _____"
                  value={blank?.sentence}
                  onChange={(e) => updateBlank(index, 'sentence', e?.target?.value)}
                />
                <Input
                  type="text"
                  placeholder="Correct answer"
                  value={blank?.answer}
                  onChange={(e) => updateBlank(index, 'answer', e?.target?.value)}
                />
              </div>
            ))}
          </div>
        )}
        {selectedGameType === 'sorting' && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <label className="text-sm font-medium text-foreground">Categories</label>
              <Button variant="outline" size="sm" iconName="Plus" onClick={addCategory}>
                Add Category
              </Button>
            </div>
            {gameConfig?.categories?.map((category, catIndex) => (
              <div key={catIndex} className="p-4 bg-muted/30 rounded-lg space-y-3">
                <div className="flex items-center justify-between">
                  <Input
                    type="text"
                    placeholder="Category name"
                    value={category?.name}
                    onChange={(e) => updateCategory(catIndex, 'name', e?.target?.value)}
                  />
                  {gameConfig?.categories?.length > 2 && (
                    <button onClick={() => removeCategory(catIndex)} className="text-destructive hover:text-destructive/80 ml-2">
                      <Icon name="Trash2" size={16} />
                    </button>
                  )}
                </div>
                <div className="space-y-2 ml-4">
                  <div className="flex items-center justify-between">
                    <label className="text-xs font-medium text-muted-foreground">Items in this category</label>
                    <Button variant="ghost" size="sm" iconName="Plus" onClick={() => addCategoryItem(catIndex)}>
                      Add Item
                    </Button>
                  </div>
                  {category?.items?.map((item, itemIndex) => {
                    const itemObj = typeof item === 'string' ? { text: item, itemType: 'text', itemImage: '' } : item;
                    return (
                      <div key={itemIndex} className="p-3 bg-card rounded-lg border border-border space-y-2">
                        <div className="flex items-center justify-between">
                          <TypeToggle
                            value={itemObj?.itemType || 'text'}
                            onChange={(val) => updateCategoryItem(catIndex, itemIndex, 'itemType', val)}
                          />
                          {category?.items?.length > 1 && (
                            <button onClick={() => removeCategoryItem(catIndex, itemIndex)} className="text-destructive hover:text-destructive/80">
                              <Icon name="Trash2" size={16} />
                            </button>
                          )}
                        </div>
                        {(itemObj?.itemType || 'text') === 'text' ? (
                          <Input
                            type="text"
                            placeholder={`Item ${itemIndex + 1}`}
                            value={itemObj?.text}
                            onChange={(e) => updateCategoryItem(catIndex, itemIndex, 'text', e?.target?.value)}
                          />
                        ) : (
                          <div className="space-y-1">
                            <GameImageUpload
                              currentImage={itemObj?.itemImage}
                              onImageChange={(url) => updateCategoryItem(catIndex, itemIndex, 'itemImage', url)}
                              label="Upload item image"
                            />
                            <Input
                              type="text"
                              placeholder="Alt text / label (optional)"
                              value={itemObj?.text}
                              onChange={(e) => updateCategoryItem(catIndex, itemIndex, 'text', e?.target?.value)}
                            />
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}
        {selectedGameType === 'image-hotspot' && (
          <div className="space-y-4">
            <div className="p-3 bg-primary/5 border border-primary/20 rounded-lg">
              <div className="flex items-start space-x-2">
                <Icon name="Info" size={16} className="text-primary mr-4 flex-shrink-0 mt-0.5" />
                <p className="text-xs text-muted-foreground">
                  Upload an image, then click anywhere on it to place numbered hotspot markers. Configure a question and answer for each hotspot.
                </p>
              </div>
            </div>
            <ImageHotspotEditor gameConfig={gameConfig} setGameConfig={setGameConfig} />
          </div>
        )}
        <div className="flex space-x-3 pt-4">
          <Button onClick={handleSaveGame} iconName="Check" className="flex-1">
            Add Game to Trail
          </Button>
          <Button variant="outline" onClick={handleCancelGame}>
            Cancel
          </Button>
        </div>
      </div>
    );
  };

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active?.id === over?.id) return;

    const oldIndex = currentPageContent?.findIndex(item => item?.id === active?.id);
    const newIndex = currentPageContent?.findIndex(item => item?.id === over?.id);

    if (oldIndex === -1 || newIndex === -1) return;

    const reorderedPageContent = arrayMove(currentPageContent, oldIndex, newIndex);

    // Rebuild full content array preserving items from other pages
    const otherContent = content?.filter(item => item?.pageNumber !== currentPage || item?.type === 'page_marker');
    const newContent = [...otherContent, ...reorderedPageContent];
    onChange(newContent);
  };

  return (
    <div className="space-y-6 md:space-y-8">
      <div className="bg-card rounded-lg p-4 md:p-6 lg:p-8 shadow-sm">
        <h2 className="text-xl md:text-2xl lg:text-3xl font-semibold text-foreground mb-4 md:mb-6">
          Creative Studio
        </h2>
        <p className="text-sm md:text-base text-muted-foreground mb-6 md:mb-8">
          Build engaging content with multimedia elements and interactive activities
        </p>

        {/* Check if current page is completion page */}
        {currentPageContent?.some(item => item?.type === 'completion') ? (
          <div className="bg-success/5 border border-success/20 rounded-lg p-6">
            <div className="flex items-center space-x-3 mb-4">
              <div className="w-12 h-12 bg-success/10 rounded-full flex items-center justify-center">
                <Icon name="Award" size={24} className="text-success" />
              </div>
              <div>
                <h3 className="text-xl font-semibold text-foreground">Completion Page</h3>
                <p className="text-sm text-muted-foreground">This page will be shown when students finish the trail</p>
              </div>
            </div>
            <div className="space-y-4">
              <Input
                label="Completion Title"
                type="text"
                placeholder="Congratulations!"
                value={currentPageContent?.[0]?.title || completionTitle}
                onChange={(e) => {
                  const updatedContent = content?.map(item => 
                    item?.pageNumber === currentPage && item?.type === 'completion'
                      ? { ...item, title: e?.target?.value }
                      : item
                  );
                  onChange(updatedContent);
                }}
              />
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">Completion Message</label>
                <textarea
                  rows={4}
                  placeholder="You have completed this learning trail! Great job!"
                  value={currentPageContent?.[0]?.message || completionMessage}
                  onChange={(e) => {
                    const updatedContent = content?.map(item => 
                      item?.pageNumber === currentPage && item?.type === 'completion'
                        ? { ...item, message: e?.target?.value }
                        : item
                    );
                    onChange(updatedContent);
                  }}
                  className="w-full px-4 py-3 bg-card border border-border rounded-md text-foreground placeholder-muted-foreground focus:outline-none focus:ring-3 focus:ring-ring focus:ring-offset-3 focus:ring-offset-background transition-organic resize-none"
                />
              </div>
              <div className="bg-primary/5 border border-primary/20 rounded-lg p-4">
                <div className="flex items-start">
                  <Icon name="Info" size={20} className="text-primary mr-3 flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-muted-foreground">
                    <p className="font-medium text-foreground mb-1">Completion Page Tips</p>
                    <ul className="space-y-1">
                      <li>• Celebrate student achievement with positive messaging</li>
                      <li>• Summarize key learnings from the trail</li>
                      <li>• Encourage students to review or explore related topics</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 md:gap-8">
            <div className="lg:col-span-2 space-y-6">
              <div className="flex items-center justify-between mb-4">
                <Select
                  options={templateOptions}
                  placeholder="Quick templates"
                  className="w-full md:w-64"
                  value={selectedTemplate}
                  onChange={handleTemplateSelect}
                />
                <Button variant="outline" iconName="Sparkles" size="sm">
                  AI Suggest
                </Button>
              </div>

              <div className="border-b border-border overflow-x-auto">
                <div className="flex space-x-1 min-w-max">
                  {contentTypes?.map((type) => (
                    <button
                      key={type?.id}
                      onClick={() => setActiveTab(type?.id)}
                      className={`flex items-center px-4 py-3 text-sm font-medium transition-organic flex-shrink-0 ${
                        activeTab === type?.id
                          ? 'bg-primary text-white' : 'bg-muted text-muted-foreground hover:bg-muted/80'
                      }`}
                    >
                      <Icon name={type?.icon} size={18} className="mr-2" />
                      {type?.label}
                    </button>
                  ))}
                </div>
              </div>

              <div className="bg-background rounded-lg p-4 md:p-6 border border-border min-h-[400px]">
                {activeTab === 'text' && (
                  <div className="space-y-4">
                    <div className="flex items-center space-x-2 mb-4">
                      <Button variant="ghost" size="sm" iconName="Bold" />
                      <Button variant="ghost" size="sm" iconName="Italic" />
                      <Button variant="ghost" size="sm" iconName="Underline" />
                      <div className="w-px h-6 bg-border mx-2" />
                      <Button variant="ghost" size="sm" iconName="List" />
                      <Button variant="ghost" size="sm" iconName="ListOrdered" />
                      <Button variant="ghost" size="sm" iconName="Link" />
                    </div>
                    <textarea
                      rows={12}
                      placeholder="Write your educational content here... Use formatting tools above to enhance readability."
                      value={textContent}
                      onChange={(e) => setTextContent(e?.target?.value)}
                      className="w-full px-4 py-3 bg-card border border-border rounded-md text-foreground placeholder-muted-foreground focus:outline-none focus:ring-3 focus:ring-ring focus:ring-offset-3 focus:ring-offset-background transition-organic resize-none"
                    />
                    <Button 
                      onClick={() => handleAddContent('text')}
                      iconName="Plus"
                      disabled={!textContent?.trim()}
                    >
                      Add Text Block
                    </Button>
                  </div>
                )}

                {activeTab === 'image' && (
                  <div className="space-y-4">
                    <input
                      type="file"
                      id="content-image"
                      accept="image/*"
                      onChange={handleImageUpload}
                      className="hidden"
                    />
                    <label
                      htmlFor="content-image"
                      className="flex flex-col items-center justify-center w-full h-64 md:h-80 border-2 border-dashed border-border rounded-lg cursor-pointer hover:border-primary transition-organic bg-muted/30"
                    >
                      <Icon name="ImagePlus" size={48} className="text-muted-foreground mb-4" />
                      <span className="text-base md:text-lg text-muted-foreground mb-2">
                        Upload Educational Images
                      </span>
                      <span className="text-sm text-muted-foreground">
                        PNG, JPG, WEBP (max 5MB)
                      </span>
                    </label>
                    <p className="text-xs md:text-sm text-muted-foreground">
                      Add diagrams, illustrations, or photos to support learning concepts
                    </p>
                  </div>
                )}

                {activeTab === 'video' && (
                  <div className="space-y-4">
                    <Input
                      label="Video URL"
                      type="url"
                      placeholder="https://youtube.com/watch?v=..."
                      value={videoUrl}
                      onChange={(e) => setVideoUrl(e?.target?.value)}
                      description="Paste a YouTube, Vimeo, or direct video link"
                    />
                    <Button 
                      onClick={() => handleAddContent('video')}
                      iconName="Plus"
                      disabled={!videoUrl?.trim()}
                    >
                      Add Video
                    </Button>
                    <div className="mt-6 p-4 bg-primary/5 rounded-lg border border-primary/20">
                      <div className="flex items-start">
                        <Icon name="Info" size={20} className="text-primary mr-3 flex-shrink-0 mt-0.5" />
                        <div className="text-sm text-muted-foreground">
                          <p className="font-medium text-foreground mb-1">Video Best Practices</p>
                          <ul className="space-y-1">
                            <li>• Keep videos under 10 minutes for better engagement</li>
                            <li>• Ensure videos have captions for accessibility</li>
                            <li>• Test playback before publishing</li>
                          </ul>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {activeTab === 'quiz' && (
                  <div className="space-y-4">
                    <Input
                      label="Quiz Question"
                      type="text"
                      placeholder="Enter your question here"
                      value={quizQuestion}
                      onChange={(e) => setQuizQuestion(e?.target?.value)}
                    />
                    <div className="space-y-3">
                      <label className="block text-sm font-medium text-foreground">
                        Answer Options
                      </label>
                      {quizOptions?.map((option, index) => (
                        <Input
                          key={index}
                          type="text"
                          placeholder={`Option ${index + 1}`}
                          value={option}
                          onChange={(e) => {
                            const newOptions = [...quizOptions];
                            newOptions[index] = e?.target?.value;
                            setQuizOptions(newOptions);
                          }}
                        />
                      ))}
                    </div>
                    <Select
                      label="Correct Answer"
                      options={[
                        { value: '', label: 'Select correct answer' },
                        ...quizOptions?.map((opt, idx) => ({
                          value: String(idx),
                          label: opt || `Option ${idx + 1}`
                        }))
                      ]}
                      value={correctAnswer}
                      onChange={setCorrectAnswer}
                    />
                    <Button 
                      onClick={() => handleAddContent('quiz')}
                      iconName={editingContentId ? "Check" : "Plus"}
                      disabled={!quizQuestion?.trim() || !quizOptions?.every(opt => opt?.trim()) || !correctAnswer}
                    >
                      {editingContentId ? 'Update Quiz Question' : 'Add Quiz Question'}
                    </Button>
                    {editingContentId && (
                      <Button 
                        onClick={handleCancelEdit}
                        variant="outline"
                        iconName="X"
                        className="ml-2"
                      >
                        Cancel
                      </Button>
                    )}
                  </div>
                )}

                {activeTab === 'game' && (
                  <div className="space-y-4">
                    {!selectedGameType ? (
                      <>
                        <h3 className="text-lg font-semibold text-foreground mb-4">
                          Interactive Game Templates
                        </h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          {gameTemplates?.map((game) => (
                            <button
                              key={game?.id}
                              onClick={() => handleAddGame(game?.id)}
                              className="p-4 bg-card border border-border rounded-lg hover:border-primary hover:bg-primary/5 transition-organic text-left active-press"
                            >
                              <div className="flex items-start">
                                <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                                  <Icon name={game?.icon} size={24} className="text-primary" />
                                </div>
                                <div>
                                  <h4 className="font-semibold text-foreground mb-1">
                                    {game?.name}
                                  </h4>
                                  <p className="text-sm text-muted-foreground">
                                    {game?.description}
                                  </p>
                                </div>
                              </div>
                            </button>
                          ))}
                        </div>
                      </>
                    ) : (
                      renderGameConfiguration()
                    )}
                  </div>
                )}
              </div>
            </div>

            <div className="lg:col-span-1">
              <div className="bg-background rounded-lg p-4 md:p-6 border border-border sticky top-24">
                <h3 className="text-base md:text-lg font-semibold text-foreground mb-4">
                  Page {currentPage} Content
                </h3>
                <div className="space-y-3 max-h-[500px] overflow-y-auto">
                  {currentPageContent?.length === 0 ? (
                    <div className="text-center py-12">
                      <Icon name="FileQuestion" size={48} className="text-muted-foreground mx-auto mb-3" />
                      <p className="text-sm text-muted-foreground">
                        No content added to this page yet
                      </p>
                    </div>
                  ) : (
                    <DndContext
                      sensors={sensors}
                      collisionDetection={closestCenter}
                      onDragEnd={handleDragEnd}
                    >
                      <SortableContext
                        items={currentPageContent?.map(item => item?.id)}
                        strategy={verticalListSortingStrategy}
                      >
                        {currentPageContent?.map((item) => (
                          <SortableContentBlock
                            key={item?.id}
                            item={item}
                            editingContentId={editingContentId}
                            onEdit={handleEditContent}
                            onRemove={handleRemoveContent}
                          />
                        ))}
                      </SortableContext>
                    </DndContext>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
      {/* Page Navigation - below Creative Studio */}
      <div className="bg-card rounded-lg p-4 md:p-6 border border-border shadow-sm">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-base md:text-lg font-semibold text-foreground">Page Navigation</h3>
          <div className="flex items-center space-x-2">
            <Button
              variant="outline"
              size="sm"
              iconName="Plus"
              onClick={handleAddNewPage}
            >
              Add Page
            </Button>
            {!hasCompletionPage && (
              <Button
                variant="outline"
                size="sm"
                iconName="Award"
                onClick={handleAddCompletionPage}
              >
                Add Completion
              </Button>
            )}
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          {allPages?.map((pageNum) => {
            const isCompletionPage = content?.some(item => item?.pageNumber === pageNum && item?.type === 'completion');
            return (
              <div key={pageNum} className="flex items-stretch">
                <button
                  onClick={() => setCurrentPage(pageNum)}
                  className={`flex items-center h-9 px-3 rounded-l-lg text-sm font-medium transition-organic ${
                    currentPage === pageNum
                      ? 'bg-primary text-white' :'bg-muted text-muted-foreground hover:bg-muted/80'
                  }`}
                >
                  {isCompletionPage ? (
                    <>
                      <Icon name="Award" size={14} className="mr-1" />
                      Completion
                    </>
                  ) : (
                    `Page ${pageNum}`
                  )}
                </button>
                {isCompletionPage ? (
                  <button
                    onClick={handleDeleteCompletionPage}
                    className={`flex items-center justify-center h-9 w-8 rounded-r-lg text-sm transition-organic border-l ${
                      currentPage === pageNum
                        ? 'bg-primary text-white border-white/30 hover:bg-destructive' :'bg-muted text-muted-foreground hover:bg-destructive/10 hover:text-destructive border-border'
                    }`}
                    title="Delete completion page"
                  >
                    <Icon name="Trash2" size={14} />
                  </button>
                ) : (
                  allPages?.length > 1 && (
                    <button
                      onClick={() => handleDeletePage(pageNum)}
                      className={`flex items-center justify-center h-9 w-8 rounded-r-lg text-sm transition-organic border-l ${
                        currentPage === pageNum
                          ? 'bg-primary text-white border-white/30 hover:bg-destructive' :'bg-muted text-muted-foreground hover:bg-destructive/10 hover:text-destructive border-border'
                      }`}
                      title="Delete page"
                    >
                      <Icon name="X" size={14} />
                    </button>
                  )
                )}
              </div>
            );
          })}
        </div>
      </div>
      <div className="bg-accent/5 rounded-lg p-4 md:p-6 border border-accent/20">
        <div className="flex items-start">
          <Icon name="Sparkles" size={24} className="text-accent mr-3 flex-shrink-0 mt-1" />
          <div>
            <h3 className="text-base md:text-lg font-semibold text-foreground mb-2">
              AI Content Suggestions
            </h3>
            <p className="text-sm md:text-base text-muted-foreground mb-3">
              Based on your journey topic, consider adding:
            </p>
            <ul className="text-sm md:text-base text-muted-foreground space-y-2">
              <li className="flex items-start">
                <span className="mr-2">•</span>
                <span>Interactive diagrams to visualize complex concepts</span>
              </li>
              <li className="flex items-start">
                <span className="mr-2">•</span>
                <span>Real-world examples to connect theory with practice</span>
              </li>
              <li className="flex items-start">
                <span className="mr-2">•</span>
                <span>Quick knowledge checks after each major section</span>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreativeStudioStep;